import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import {
  defaultUserStatsDateRange,
  formatReportCount,
  validateUserStatsDateRange,
} from './userStatisticsReportConstants';

const DEFAULT_RANGE = defaultUserStatsDateRange();

const EMPTY_FILTERS = {
  startDateInput: DEFAULT_RANGE.startDateInput,
  endDateInput: DEFAULT_RANGE.endDateInput,
};

/**
 * §51.6 im-chat V1 骨架：社交會話新建／活躍；訊息量後補。
 */
export default function ImChatReportPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [summary, setSummary] = useState({
    socialConversationCreatedCount: 0,
    socialConversationActiveCount: 0,
    socialMessageCount: 0,
  });
  const [meta, setMeta] = useState({ moduleEnabled: true, chatEnabledSkipped: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load(nextFilters = filters) {
    const rangeError = validateUserStatsDateRange(
      nextFilters.startDateInput,
      nextFilters.endDateInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.imChatReportInterval({
        start: nextFilters.startDateInput,
        end: nextFilters.endDateInput,
      });
      setMeta({
        moduleEnabled: data?.moduleEnabled !== false,
        chatEnabledSkipped: !!data?.chatEnabledSkipped,
      });
      setSummary({
        socialConversationCreatedCount: data?.socialConversationCreatedCount ?? 0,
        socialConversationActiveCount: data?.socialConversationActiveCount ?? 0,
        socialMessageCount: data?.socialMessageCount ?? 0,
      });
    } catch (err) {
      setError(err.message || '載入失敗');
      setSummary({
        socialConversationCreatedCount: 0,
        socialConversationActiveCount: 0,
        socialMessageCount: 0,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onFilterChange(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="page-panel">
      <div className="page-header">
        <h2>聊天報表</h2>
        <p className="page-desc">社交 IM 會話新建／活躍（排除客服通道）。訊息量與貼圖經濟後續補齊。</p>
      </div>

      <div className="toolbar filters-row">
        <label>
          開始日期
          <input
            type="date"
            value={filters.startDateInput}
            onChange={(e) => onFilterChange('startDateInput', e.target.value)}
          />
        </label>
        <label>
          結束日期
          <input
            type="date"
            value={filters.endDateInput}
            onChange={(e) => onFilterChange('endDateInput', e.target.value)}
          />
        </label>
        <button type="button" className="btn-primary" disabled={loading} onClick={() => load()}>
          {loading ? '查詢中…' : '查詢'}
        </button>
      </div>

      {error ? <div className="form-error">{error}</div> : null}
      {!meta.moduleEnabled ? (
        <div className="form-error">
          聊天報表模組已關閉（formosa.report.modules.enabled.im-chat=false）
        </div>
      ) : null}
      {meta.chatEnabledSkipped ? (
        <div className="form-error">聊天系統已關閉（chat.enabled=false），無社交會話資料</div>
      ) : null}

      <div
        className="stats-summary-cards"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <SummaryCard
          label="新建社交會話"
          value={formatReportCount(summary.socialConversationCreatedCount)}
        />
        <SummaryCard
          label="活躍社交會話"
          value={formatReportCount(summary.socialConversationActiveCount)}
        />
        <SummaryCard
          label="訊息量（後補）"
          value={formatReportCount(summary.socialMessageCount)}
        />
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}
